import type Api from "../services/api";
import type { DriveStore } from "../store/reducers";
import type { ITrailer } from "../model/Trailer";
import * as logger from "../utils/logger";
import constants from "../constants";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { makeLegacyTrailerCache, makeTrailerAsDeviceCache, makeTrailerCache, makeTrailerDeviceCache } from "../utils/storage/cache";
import { getAsset } from "./deviceApiHelpers";
import { noEntityIds } from "./common";
import { usesTrailerDeviceIdentity } from "./trailerAttachmentDeviceLink";

/**
 * Page size for scanning the trailer devices that match a legacy trailer's name for the one whose
 * tmpTrailerId links to it. The name search is a prefix match and trailer names are not unique, so
 * the scan pages until the link is found or the matches run out.
 */
const TRAILER_NAME_SEARCH_PAGE_SIZE = 100;

type TClassification = { isValid: true } | { isValid: false; translatedId?: string };

const VALID: TClassification = { isValid: true };
const UNRESOLVED: TClassification = { isValid: false };

/**
 * MYG-157137: DRIVE.ENABLE_TRAILER_DEVICE_LINK_API.RELEASE decides whether a trailer is identified by
 * its own Device.id (on) or by the legacy Trailer.Id held in Device.tmpTrailerId (off). Queued items
 * keep the id they were created with but are sent through whichever API is active when they sync, and
 * carry no marker of the identity they were created under. An id from the other identity is rejected
 * by the server and the item is dropped, so before an item is sent its trailer id is classified and,
 * when it belongs to the other identity, translated.
 *
 * Only positive evidence translates an id: a device whose tmpTrailerId links the two identities. An
 * id that is valid for the active identity, or that cannot be classified (offline, unknown trailer),
 * is left as is, which is the behaviour before this reconciliation existed.
 */
export class TrailerIdentityReconciler {
    /**
     * Ids confirmed valid, keyed with the identity they were confirmed for. The identity is latched for
     * the session, but a read taken before the flags load answers legacy without latching, so an id
     * confirmed then must not count as confirmed once the flag has latched the device identity.
     */
    private readonly confirmedIds = new Set<string>();

    constructor(private readonly api: Api, private readonly store: DriveStore) {}

    /** Resolves to the ids that belong to the other identity, mapped to their id in the active one. */
    reconcile(trailerIds: string[]): Promise<IIdDictionary<string>> {
        const usesDeviceIdentity = usesTrailerDeviceIdentity(this.store);
        const confirmedKey = (id: string) => `${ usesDeviceIdentity }:${ id }`;
        const ids = trailerIds.filter((id, index) =>
            !!id && noEntityIds.indexOf(id) === -1 && trailerIds.indexOf(id) === index && !this.confirmedIds.has(confirmedKey(id)));
        if (!ids.length) {
            return Promise.resolve({});
        }

        const classifyFromCache = this.makeCacheClassifier(usesDeviceIdentity);
        const translations: IIdDictionary<string> = {};

        return Promise.all(ids.map(id => {
            const cached = classifyFromCache(id);
            return (cached ? Promise.resolve(cached) : this.classifyFromServer(id, usesDeviceIdentity))
                .then(classification => {
                    if (classification.isValid) {
                        this.confirmedIds.add(confirmedKey(id));
                    } else if (classification.translatedId) {
                        translations[id] = classification.translatedId;
                        logger.log(
                            logger.Source.SYNC,
                            `Trailer id ${ id } belongs to the ${ usesDeviceIdentity ? "legacy trailer" : "trailer device" } identity; syncing it as ${ classification.translatedId }`,
                            this.api.getUser()
                        );
                    }
                });
        })).then(() => translations, () => translations)
            // The flags can load and latch while the lookups are in flight, and the push then encodes for
            // the latched identity, so the ids are classified again for it. The latch is only ever set
            // once, so this re-runs at most once.
            .then(result => usesTrailerDeviceIdentity(this.store) === usesDeviceIdentity ? result : this.reconcile(trailerIds));
    }

    /**
     * Classifies from the trailer caches, answering undefined when they cannot tell. The device-backed
     * slots hold both identities of a trailer (Device.id as the key, tmpTrailerId as a property).
     */
    private makeCacheClassifier(usesDeviceIdentity: boolean): (id: string) => TClassification | undefined {
        const userId = this.api.getUser()?.id;
        const legacyTrailers = makeLegacyTrailerCache(this.store).getDict(userId);
        const trailerAsDevices = makeTrailerAsDeviceCache(this.store).getDict(userId);
        const trailerDevices: IIdDictionary<{ id: string; tmpTrailerId?: string }> = {
            ...makeTrailerDeviceCache(this.store).getDict(userId),
            ...trailerAsDevices
        };
        const deviceIdByLegacyId: IIdDictionary<string> = {};
        Object.keys(trailerDevices).forEach(deviceId => {
            const legacyId = trailerDevices[deviceId]?.tmpTrailerId;
            legacyId && (deviceIdByLegacyId[legacyId] = deviceId);
        });
        const activeTrailers = usesDeviceIdentity ? trailerAsDevices : legacyTrailers;
        const otherTrailers = usesDeviceIdentity ? legacyTrailers : trailerAsDevices;

        return id => {
            if (activeTrailers[id]) {
                return VALID;
            }
            if (otherTrailers[id]?._isTemp) {
                // A trailer created offline has a local id in either identity; it only has to be
                // findable in the active slot for the offline-attachment flow to create it. It is
                // moved rather than copied: processOfflineAttachment only clears the active slot, so
                // a copy left behind would linger as a phantom _isTemp trailer under the other flag state.
                const tempTrailer = otherTrailers[id];
                makeTrailerCache(this.store).add(tempTrailer, userId);
                (usesDeviceIdentity ? makeLegacyTrailerCache(this.store) : makeTrailerAsDeviceCache(this.store)).remove(tempTrailer, userId);
                return VALID;
            }
            if (usesDeviceIdentity) {
                if (trailerDevices[id]) {
                    return VALID;
                }
                return deviceIdByLegacyId[id] ? { isValid: false, translatedId: deviceIdByLegacyId[id] } : undefined;
            }
            if (deviceIdByLegacyId[id]) {
                return VALID;
            }
            const legacyId = trailerDevices[id]?.tmpTrailerId;
            return legacyId ? { isValid: false, translatedId: legacyId } : undefined;
        };
    }

    /** Each lookup failure is treated as "not found": a failed lookup can never produce a translation. */
    private classifyFromServer(id: string, usesDeviceIdentity: boolean): Promise<TClassification> {
        const getFirst = <T>(search: object, typeName: string): Promise<T[]> =>
            getAsset(this.api, search, typeName).then((results: T[]) => results || [], () => []);

        if (!usesDeviceIdentity) {
            return getFirst<ITrailer>({ id }, "Trailer").then(trailers => trailers.length
                ? VALID
                : getFirst<IDevice>({ id }, "Device").then(devices => {
                    const legacyId = devices[0]?.tmpTrailerId;
                    return legacyId ? { isValid: false, translatedId: legacyId } : UNRESOLVED;
                }));
        }

        return getFirst<IDevice>({ id }, "Device").then(devices => {
            if (devices.length) {
                return VALID;
            }
            return getFirst<ITrailer>({ id }, "Trailer").then(trailers => {
                const name = trailers[0]?.name;
                if (!name) {
                    return UNRESOLVED;
                }
                return this.findTrailerDeviceByLegacyId(name, id)
                    .then(deviceId => deviceId ? { isValid: false, translatedId: deviceId } : UNRESOLVED);
            });
        });
    }

    /**
     * Pages the trailer devices matching `name`, in id order, for the one whose tmpTrailerId is
     * `legacyId`. A failed page ends the scan as "not found", like every other lookup here.
     */
    private findTrailerDeviceByLegacyId(name: string, legacyId: string, offset?: string): Promise<string | undefined> {
        return new Promise<IDevice[]>((resolve, reject) => {
            this.api.call("Get", {
                typeName: "Device",
                search: { name, groups: [{ id: constants.GROUPS.TRAILER_GROUP_ID }] },
                resultsLimit: TRAILER_NAME_SEARCH_PAGE_SIZE,
                sort: { sortBy: "id", sortDirection: "asc", offset }
            }, resolve, reject);
        }).then(page => {
            const devices = page || [];
            const match = devices.find(device => device.tmpTrailerId === legacyId);
            if (match) {
                return match.id;
            }
            const lastId = devices[devices.length - 1]?.id;
            const hasMore = devices.length === TRAILER_NAME_SEARCH_PAGE_SIZE && !!lastId && lastId !== offset;
            return hasMore ? this.findTrailerDeviceByLegacyId(name, legacyId, lastId) : undefined;
        }, () => undefined);
    }
}
